/* eslint-disable complexity */

import React from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';

import { CombinedSubEntityForecastData } from '@futrli/views/combiners/subEntityForecastsCombiner';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import {
  ConsolidatedForecastDatasource,
  ConsolidatedForecastDatasourceProps
} from '../form-elements/consolidated-forecast-datasource/consolidated-forecast-datasource';
import { ForecastDuration, ForecastDurationProps } from '../form-elements/forecast-duration';
import { ForecastFormErrors, ForecastTypeOption } from '../../types';
import { ForecastFrom, ForecastFromProps } from '../form-elements/forecast-from';
import { ForecastName, ForecastNameProps } from '../form-elements/forecast-name';
import { ForecastSourceForecast, ForecastSourceForecastProps } from '../form-elements/forecast-source-forecast';
import { NewConsolidatedForecastDataSource, OrgOrConForecastListItem } from '../../../dashboard/types';
import { SubEntityForecasts } from '../form-elements/sub-entity-forecasts/sub-entity-forecasts';

import styles from '../form-elements/elements.scss';

interface CreateNewConsolidatedForecastFormProps {
  datasource: NewConsolidatedForecastDataSource | '';
  duration: string;
  errors?: ForecastFormErrors;
  forecasts?: OrgOrConForecastListItem[];
  from: string;
  name: string;
  sourceForecast?: string;
  onChangeDatasource: ConsolidatedForecastDatasourceProps['onChange'];
  onChangeDuration: ForecastDurationProps['onChange'];
  onChangeFrom: ForecastFromProps['onChange'];
  onChangeName: ForecastNameProps['onChange'];
  onChangeSourceForecast: ForecastSourceForecastProps['onChange'];
  onChangeSourceSubEntityForecast: (orgId: string, forecastId: string) => void;
  onChangeSourceSubEntity: (orgId: string) => void;
  revalidate: () => void;
  sourceSubEntityForecasts: Record<string, string>;
  subEntityForecasts?: CombinedSubEntityForecastData;
}

export const CreateNewConsolidatedForecastForm = ({
  errors = {},
  datasource,
  duration,
  forecasts,
  from,
  name,
  sourceForecast,
  onChangeDatasource,
  onChangeDuration,
  onChangeFrom,
  onChangeName,
  onChangeSourceForecast,
  onChangeSourceSubEntityForecast,
  onChangeSourceSubEntity,
  sourceSubEntityForecasts,
  subEntityForecasts
}: CreateNewConsolidatedForecastFormProps) => {
  const intl = useIntl();

  return (
    <Box className={styles.carbonFormFixes}>
      <WizardRequiredFieldInfo />
      <ForecastName
        value={name}
        onChange={onChangeName}
        label={intl.formatMessage({ id: 'predict.create_forecast.forecast_name', defaultMessage: 'Forecast name' })}
        error={errors.name?.message}
      />
      <ForecastFrom
        value={from}
        onChange={onChangeFrom}
        label={intl.formatMessage({ id: 'common.start_date', defaultMessage: 'Start date' })}
        error={errors.from?.message}
      />
      <ForecastDuration
        type={ForecastTypeOption.Fixed}
        value={duration}
        onChange={onChangeDuration}
        legend={intl.formatMessage({ id: 'predict.create_forecast.duration', defaultMessage: 'Duration' })}
        error={errors.duration?.message}
      />
      <ConsolidatedForecastDatasource
        type="forecast"
        value={datasource}
        onChange={onChangeDatasource}
        error={errors.datasource?.message}
        disableCombineSubEntityForecatstsOption={
          !!subEntityForecasts && !Object.values(subEntityForecasts).some(entity => entity.forecasts.length > 0)
        }
      />

      {datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING && (
        <ForecastSourceForecast
          type="forecast"
          value={sourceForecast ?? ''}
          onChange={onChangeSourceForecast}
          error={errors.sourceForecast?.message}
          forecasts={forecasts}
        />
      )}

      {datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING_SUB_ENTITY_FORECASTS && (
        <SubEntityForecasts
          errors={errors}
          onChangeSourceSubEntity={onChangeSourceSubEntity}
          onChangeSourceSubEntityForecast={onChangeSourceSubEntityForecast}
          subEntityForecasts={subEntityForecasts}
          sourceSubEntityForecasts={sourceSubEntityForecasts}
          type="forecast"
        />
      )}
    </Box>
  );
};
